// src/pages/admin/BrokerContentPanel.jsx
import React, { useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import app from "../../firebase";
import {
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiArrowUp,
  FiArrowDown,
  FiSave,
  FiX,
  FiUploadCloud,
  FiFile,
  FiCheckCircle,
} from "react-icons/fi";

const db = getFirestore(app);
const storage = getStorage(app);

const brand = {
  btnPrimary: "bg-[#002b5c] text-white hover:bg-[#082f6d]",
  btnGhost: "bg-white border border-gray-200 hover:bg-gray-50",
  input:
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-[#002b5c]/25 focus:border-[#002b5c]/40",
};

const MAX_FILE_MB = 100;

// One text/file field row inside the add/edit form.
function FieldInput({ field, value, file, onTextChange, onFileChange }) {
  if (field.type === "file") {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">{field.label}</label>
        <div className="flex items-center gap-3">
          <label
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm cursor-pointer ${brand.btnGhost}`}
          >
            <FiUploadCloud /> Choose file
            <input
              type="file"
              accept={field.accept}
              className="hidden"
              onChange={(e) => onFileChange(field.key, e.target.files?.[0] || null)}
            />
          </label>
          {file ? (
            <span className="text-sm text-gray-700 flex items-center gap-1 truncate">
              <FiFile className="shrink-0" /> {file.name}
            </span>
          ) : value ? (
            <span className="text-sm text-green-700 flex items-center gap-1 truncate">
              <FiCheckCircle className="shrink-0" /> File uploaded
            </span>
          ) : (
            <span className="text-sm text-gray-400">{field.placeholder}</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <input
      className={brand.input}
      placeholder={field.placeholder}
      value={value || ""}
      onChange={(e) => onTextChange(field.key, e.target.value)}
    />
  );
}

// Reusable manager for a simple ordered list collection.
// `fields` describes the editable fields: [{ key, label, placeholder, type: 'text' | 'file', accept, storagePath, required }]
function ContentListEditor({ collectionName, fields, emptyLabel }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newValues, setNewValues] = useState({});
  const [newFiles, setNewFiles] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [editFiles, setEditFiles] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");

  useEffect(() => {
    const q = query(collection(db, collectionName), orderBy("order", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [collectionName]);

  const validateSize = (file) => {
    if (!file) return true;
    return file.size <= MAX_FILE_MB * 1024 * 1024;
  };

  // Browsers frequently report an empty or wrong MIME type for certain
  // files (screen recordings saved as .mov are a common culprit). Falling
  // back to extension-based detection keeps uploads working reliably and
  // matches what the Storage security rules check against.
  const guessContentType = (fileName, browserType, field) => {
    if (browserType) return browserType;
    const ext = (fileName.split(".").pop() || "").toLowerCase();
    const map = {
      mov: "video/quicktime",
      mp4: "video/mp4",
      m4v: "video/mp4",
      webm: "video/webm",
      avi: "video/x-msvideo",
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
    };
    return map[ext] || (field?.type === "file" && field.accept?.startsWith("video") ? "video/mp4" : "application/octet-stream");
  };

  const uploadFieldFile = async (field, file) => {
    const path = `${field.storagePath || collectionName}/${Date.now()}_${file.name}`;
    const fileRef = ref(storage, path);
    const contentType = guessContentType(file.name, file.type, field);
    await uploadBytes(fileRef, file, { contentType });
    return getDownloadURL(fileRef);
  };

  const resetNew = () => {
    setNewValues({});
    setNewFiles({});
  };

  const handleAdd = async () => {
    const missingRequired = fields.some(
      (f) => f.required && f.type !== "file" && !String(newValues[f.key] || "").trim()
    );
    if (missingRequired) return;

    const oversized = fields.find((f) => f.type === "file" && !validateSize(newFiles[f.key]));
    if (oversized) {
      alert(`File is too large. Max size is ${MAX_FILE_MB}MB.`);
      return;
    }

    setSaving(true);
    try {
      const payload = {};
      for (const f of fields) {
        if (f.type === "file") {
          const file = newFiles[f.key];
          if (file) {
            setUploadStatus(`Uploading ${f.label}…`);
            payload[f.key] = await uploadFieldFile(f, file);
          } else {
            payload[f.key] = "";
          }
        } else {
          payload[f.key] = newValues[f.key] || "";
        }
      }
      setUploadStatus("");

      const maxOrder = items.reduce((m, i) => Math.max(m, i.order ?? 0), 0);
      await addDoc(collection(db, collectionName), {
        ...payload,
        order: maxOrder + 1,
        createdAt: serverTimestamp(),
      });
      resetNew();
      setAdding(false);
    } catch (err) {
      console.error(err);
      alert("Something went wrong while saving. Please try again.");
    } finally {
      setSaving(false);
      setUploadStatus("");
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValues({ ...item });
    setEditFiles({});
  };

  const handleSaveEdit = async () => {
    const oversized = fields.find((f) => f.type === "file" && !validateSize(editFiles[f.key]));
    if (oversized) {
      alert(`File is too large. Max size is ${MAX_FILE_MB}MB.`);
      return;
    }

    setSaving(true);
    try {
      const payload = {};
      for (const f of fields) {
        if (f.type === "file") {
          const file = editFiles[f.key];
          if (file) {
            setUploadStatus(`Uploading ${f.label}…`);
            payload[f.key] = await uploadFieldFile(f, file);
          } else {
            payload[f.key] = editValues[f.key] || ""; // keep existing URL if no new file chosen
          }
        } else {
          payload[f.key] = editValues[f.key] || "";
        }
      }
      setUploadStatus("");

      await updateDoc(doc(db, collectionName, editingId), payload);
      setEditingId(null);
      setEditValues({});
      setEditFiles({});
    } catch (err) {
      console.error(err);
      alert("Something went wrong while saving. Please try again.");
    } finally {
      setSaving(false);
      setUploadStatus("");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      // Best-effort cleanup of any uploaded files for this item
      await Promise.all(
        fields
          .filter((f) => f.type === "file" && item[f.key])
          .map(async (f) => {
            try {
              await deleteObject(ref(storage, item[f.key]));
            } catch {
              // File may already be gone, or URL wasn't a Storage ref — ignore
            }
          })
      );
      await deleteDoc(doc(db, collectionName, item.id));
    } catch (err) {
      console.error(err);
    }
  };

  const moveItem = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    const a = items[index];
    const b = items[targetIndex];
    try {
      await Promise.all([
        updateDoc(doc(db, collectionName, a.id), { order: b.order ?? targetIndex }),
        updateDoc(doc(db, collectionName, b.id), { order: a.order ?? index }),
      ]);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      {loading ? (
        <div className="text-gray-500 text-sm">Loading…</div>
      ) : items.length === 0 && !adding ? (
        <div className="text-gray-500 text-sm mb-4">{emptyLabel}</div>
      ) : (
        <div className="space-y-2 mb-4">
          {items.map((item, index) => (
            <div key={item.id} className="border border-gray-200 rounded-xl p-4">
              {editingId === item.id ? (
                <div className="space-y-3">
                  {fields.map((f) => (
                    <FieldInput
                      key={f.key}
                      field={f}
                      value={editValues[f.key]}
                      file={editFiles[f.key]}
                      onTextChange={(k, v) => setEditValues((p) => ({ ...p, [k]: v }))}
                      onFileChange={(k, file) => setEditFiles((p) => ({ ...p, [k]: file }))}
                    />
                  ))}
                  {uploadStatus && <p className="text-xs text-gray-500">{uploadStatus}</p>}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleSaveEdit}
                      disabled={saving}
                      className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm ${brand.btnPrimary} disabled:opacity-60`}
                    >
                      <FiSave /> {saving ? "Saving…" : "Save"}
                    </button>
                    <button
                      onClick={() => { setEditingId(null); setEditValues({}); setEditFiles({}); }}
                      className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm ${brand.btnGhost}`}
                    >
                      <FiX /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-1 pt-1">
                    <button
                      onClick={() => moveItem(index, -1)}
                      disabled={index === 0}
                      className="text-gray-400 hover:text-[#002b5c] disabled:opacity-30"
                      title="Move up"
                    >
                      <FiArrowUp />
                    </button>
                    <button
                      onClick={() => moveItem(index, 1)}
                      disabled={index === items.length - 1}
                      className="text-gray-400 hover:text-[#002b5c] disabled:opacity-30"
                      title="Move down"
                    >
                      <FiArrowDown />
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    {fields.map((f) =>
                      f.type === "file" ? (
                        item[f.key] ? (
                          <a
                            key={f.key}
                            href={item[f.key]}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-[#002b5c] underline flex items-center gap-1"
                          >
                            <FiFile /> View file
                          </a>
                        ) : null
                      ) : (
                        <p
                          key={f.key}
                          className={f.key === fields[0].key ? "font-medium" : "text-sm text-gray-500 break-all"}
                        >
                          {item[f.key]}
                        </p>
                      )
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => startEdit(item)}
                      className="h-9 px-3 text-sm rounded-lg bg-white border hover:bg-gray-50"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="h-9 px-3 text-sm rounded-lg bg-[#e31c3d] text-white hover:opacity-95"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          {fields.map((f) => (
            <FieldInput
              key={f.key}
              field={f}
              value={newValues[f.key]}
              file={newFiles[f.key]}
              onTextChange={(k, v) => setNewValues((p) => ({ ...p, [k]: v }))}
              onFileChange={(k, file) => setNewFiles((p) => ({ ...p, [k]: file }))}
            />
          ))}
          {uploadStatus && <p className="text-xs text-gray-500">{uploadStatus}</p>}
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleAdd}
              disabled={saving}
              className={`rounded-lg px-4 py-2 text-sm ${brand.btnPrimary} disabled:opacity-60`}
            >
              {saving ? "Adding…" : "Add"}
            </button>
            <button
              onClick={() => { setAdding(false); resetNew(); }}
              className={`rounded-lg px-4 py-2 text-sm ${brand.btnGhost}`}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm ${brand.btnGhost}`}
        >
          <FiPlus /> Add item
        </button>
      )}
    </div>
  );
}

export default function BrokerContentPanel() {
  const [tab, setTab] = useState("tutorials");

  return (
    <div>
      <div className="flex gap-2 mb-5 border-b border-gray-200">
        {[
          { key: "tutorials", label: "Tutorials" },
          { key: "resources", label: "Resources" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
              tab === t.key
                ? "border-[#e31c3d] text-[#002b5c]"
                : "border-transparent text-gray-500 hover:text-[#002b5c]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "tutorials" && (
        <ContentListEditor
          collectionName="broker_tutorials"
          emptyLabel="No tutorials yet. Add the first one below."
          fields={[
            { key: "title", label: "Title", placeholder: "Title (e.g. Getting Started as a Broker)", required: true, type: "text" },
            { key: "description", label: "Description", placeholder: "Short description shown under the title", required: true, type: "text" },
            { key: "duration", label: "Duration", placeholder: "Duration (e.g. 6 min)", type: "text" },
            {
              key: "videoUrl",
              label: "Video file",
              placeholder: "No file uploaded yet",
              type: "file",
              accept: "video/*",
              storagePath: "broker_tutorials",
            },
          ]}
        />
      )}

      {tab === "resources" && (
        <ContentListEditor
          collectionName="broker_resources"
          emptyLabel="No resources yet. Add the first one below."
          fields={[
            { key: "title", label: "Title", placeholder: "Title (e.g. Broker Handbook)", required: true, type: "text" },
            { key: "note", label: "Note", placeholder: "Short note shown under the title", required: true, type: "text" },
            {
              key: "fileUrl",
              label: "File",
              placeholder: "No file uploaded yet",
              type: "file",
              accept: ".pdf,.doc,.docx,.png,.jpg,.jpeg",
              storagePath: "broker_resources",
            },
          ]}
        />
      )}
    </div>
  );
}
